import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Card,
  FormControl
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Code as CodeIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import api from '../services/api';
import AwsComputeSettings from './AwsComputeSettings';
import { SupportedInstanceType } from '../types/providers';

interface ProviderConfigFormProps {
  providerId?: string;
  regionTypes?: Record<string, string>;
  onRegionTypesChange?: (value: Record<string, string>) => void;
  onValidityChange?: (valid: boolean) => void;
  config: Record<string, any>;
  onChange: (config: Record<string, any>) => void;
  credentials: Record<string, any>;
  onCredentialsChange: (credentials: Record<string, any>) => void;
  providerType: 'aws' | 'gcp' | 'azure' | 'openstack';
  configSchema?: Record<string, any>;
  credentialsSchema?: Record<string, any>;
  disabled?: boolean;
}

interface ParsedSchema {
  primitiveFields: Array<{
    name: string;
    type: string;
    description?: string;
    required: boolean;
  }>;
  objectFields: Array<{
    name: string;
    type: 'object';
    description?: string;
    required: boolean;
    properties: Array<{
      name: string;
      type: string;
      description?: string;
      required: boolean;
      items?: {
        type: string;
        properties?: Array<{
          name: string;
          type: string;
          description?: string;
          required: boolean;
        }>;
      };
    }>;
  }>;
  requiredFields: string[];
}

const ProviderConfigForm: React.FC<ProviderConfigFormProps> = ({
  config,
  onChange,
  providerId, regionTypes = {}, onRegionTypesChange = () => {}, onValidityChange,
  credentials,
  onCredentialsChange,
  providerType,
  configSchema,
  credentialsSchema,
  disabled = false
}) => {
  const [viewMode, setViewMode] = useState<'form' | 'json'>('form');
  const [jsonString, setJsonString] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [supportedInstanceTypes, setSupportedInstanceTypes] = useState<SupportedInstanceType[]>([]);

  // Parse schema to categorize fields
  const parseSchema = (schema: Record<string, any>): ParsedSchema => {
    if (!schema?.properties) {
      return { primitiveFields: [], objectFields: [], requiredFields: [] };
    }

    const requiredFields = schema.required || [];
    const primitiveFields: Array<{
      name: string;
      type: string;
      description?: string;
      required: boolean;
    }> = [];
    const objectFields: Array<{
      name: string;
      type: 'object';
      description?: string;
      required: boolean;
      properties: Array<{
        name: string;
        type: string;
        description?: string;
        required: boolean;
      }>;
    }> = [];

    Object.entries(schema.properties).forEach(([fieldName, fieldSchema]: [string, any]) => {
      const isRequired = requiredFields.includes(fieldName);
      
      if (fieldSchema.type === 'object' && fieldSchema.properties) {
        // This is a nested object
        const nestedProperties = Object.entries(fieldSchema.properties).map(([propName, propSchema]: [string, any]) => ({
          name: propName,
          type: propSchema.type || 'string',
          description: propSchema.description,
          required: fieldSchema.required?.includes(propName) || false,
          items: propSchema.items ? {
            type: propSchema.items.type || 'string',
            properties: propSchema.items.properties ? Object.entries(propSchema.items.properties).map(([itemPropName, itemPropSchema]: [string, any]) => ({
              name: itemPropName,
              type: itemPropSchema.type || 'string',
              description: itemPropSchema.description,
              required: propSchema.items.required?.includes(itemPropName) || false
            })) : undefined
          } : undefined
        }));

        objectFields.push({
          name: fieldName,
          type: 'object',
          description: fieldSchema.description,
          required: isRequired,
          properties: nestedProperties
        });
      } else {
        // This is a primitive field
        primitiveFields.push({
          name: fieldName,
          type: fieldSchema.type || 'string',
          description: fieldSchema.description,
          required: isRequired
        });
      }
    });

    return { primitiveFields, objectFields, requiredFields };
  };

  // Parse the schema
  const parsedSchema = parseSchema(configSchema || {});
  const credentialsSchemaData = (credentialsSchema?.properties as Record<string, any>) || {};

  // Update JSON string when config changes
  useEffect(() => {
    setJsonString(JSON.stringify(config, null, 2));
  }, [config]);

  // Load supported instance types for the provider
  useEffect(() => {
    const loadSupportedInstanceTypes = async () => {
      try {
        const instanceTypes = await api.getSupportedInstanceTypes(providerType);
        setSupportedInstanceTypes(instanceTypes);
      } catch (error) {
        console.error('Failed to load supported instance types:', error);
      }
    };

    if (providerType) {
      loadSupportedInstanceTypes();
    }
  }, [providerType]);

  const handleFieldChange = (fieldName: string, value: any) => {
    const newConfig = { ...config };
    newConfig[fieldName] = value;
    onChange(newConfig);
  };

  const handleNestedFieldChange = (objectName: string, fieldName: string, value: any) => {
    const newConfig = { ...config };
    if (!newConfig[objectName] || typeof newConfig[objectName] !== 'object') {
      newConfig[objectName] = {};
    }
    newConfig[objectName][fieldName] = value;
    onChange(newConfig);
  };

  const handleAddField = (fieldName: string, value: any) => {
    const newConfig = { ...config };
    newConfig[fieldName] = value;
    onChange(newConfig);
  };

  const handleAddNestedField = (objectName: string, fieldName: string, value: any) => {
    const newConfig = { ...config };
    if (!newConfig[objectName] || typeof newConfig[objectName] !== 'object') {
      newConfig[objectName] = {};
    }
    newConfig[objectName][fieldName] = value;
    onChange(newConfig);
  };

  const handleRemoveField = (fieldName: string) => {
    const newConfig = { ...config };
    delete newConfig[fieldName];
    onChange(newConfig);
  };

  const handleRemoveNestedField = (objectName: string, fieldName: string) => {
    const newConfig = { ...config };
    if (newConfig[objectName] && typeof newConfig[objectName] === 'object') {
      delete newConfig[objectName][fieldName];
      if (Object.keys(newConfig[objectName]).length === 0) {
        delete newConfig[objectName];
      }
    }
    onChange(newConfig);
  };

  const handleCredentialChange = (fieldName: string, value: any) => {
    const newCredentials = { ...credentials, [fieldName]: value };
    onCredentialsChange(newCredentials);
  };

  // Array field handlers
  const handleAddArrayItem = (objectName: string, fieldName: string) => {
    const newConfig = { ...config };
    if (!newConfig[objectName] || typeof newConfig[objectName] !== 'object') {
      newConfig[objectName] = {};
    }
    if (!Array.isArray(newConfig[objectName][fieldName])) {
      newConfig[objectName][fieldName] = [];
    }
    
    // Find the field schema to get default values
    const objectField = parsedSchema.objectFields.find(f => f.name === objectName);
    const fieldSchema = objectField?.properties.find(p => p.name === fieldName);
    
    if (fieldSchema?.items?.properties) {
      // Create a new object with default values
      const newItem: Record<string, any> = {};
      fieldSchema.items.properties.forEach(prop => {
        if (prop.name === 'architecture' && fieldName === 'instance_types') {
          newItem[prop.name] = config.bot_runtime === 'docker' && config.container_fleet?.artifact_arch !== 'amd64' ? 'arm64' : 'x86_64';
        } else if (prop.name === 'bots_per_host' && fieldName === 'instance_types') {
          newItem[prop.name] = config.container_fleet?.bots_per_host ?? 5;
        } else {
          newItem[prop.name] = getDefaultValue(prop.type);
        }
      });
      newConfig[objectName][fieldName].push(newItem);
    } else {
      // Simple array item
      newConfig[objectName][fieldName].push(getDefaultValue(fieldSchema?.items?.type || 'string'));
    }
    
    onChange(newConfig);
  };

  const handleInstanceTypeChange = (objectName: string, fieldName: string, index: number, instanceType: string) => {
    const newConfig = { ...config };
    if (newConfig[objectName] && Array.isArray(newConfig[objectName][fieldName])) {
      if (!newConfig[objectName][fieldName][index]) {
        newConfig[objectName][fieldName][index] = {};
      }
      
      // Update instance type
      newConfig[objectName][fieldName][index]['type'] = instanceType;
      
      // Auto-set architecture based on selected instance type
      const selectedInstanceType = supportedInstanceTypes.find(it => it.instance_type === instanceType);
      if (selectedInstanceType) {
        newConfig[objectName][fieldName][index]['architecture'] = selectedInstanceType.architecture;
      }
    }
    onChange(newConfig);
  };

  const handleRemoveArrayItem = (objectName: string, fieldName: string, index: number) => {
    const newConfig = { ...config };
    if (newConfig[objectName] && Array.isArray(newConfig[objectName][fieldName])) {
      newConfig[objectName][fieldName].splice(index, 1);
      if (newConfig[objectName][fieldName].length === 0) {
        delete newConfig[objectName][fieldName];
      }
    }
    onChange(newConfig);
  };

  const handleUpdateArrayItem = (objectName: string, fieldName: string, index: number, itemFieldName: string, value: any) => {
    const newConfig = { ...config };
    if (newConfig[objectName] && Array.isArray(newConfig[objectName][fieldName])) {
      if (!newConfig[objectName][fieldName][index]) {
        newConfig[objectName][fieldName][index] = {};
      }
      newConfig[objectName][fieldName][index][itemFieldName] = value;
    }
    onChange(newConfig);
  };

  const handleJsonChange = (value: string) => {
    setJsonString(value);
    
    try {
      const parsed = JSON.parse(value);
      
      // Validate required fields
      const missingRequired = parsedSchema.requiredFields.filter(field => 
        !(field in parsed) || parsed[field] === '' || parsed[field] === null || parsed[field] === undefined
      );
      
      if (missingRequired.length > 0) {
        setJsonError(`Missing required fields: ${missingRequired.join(', ')}`);
        return;
      }
      
      setJsonError(null);
      onChange(parsed);
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON');
    }
  };

  // Get available optional fields for autocomplete
  const getAvailableOptionalFields = (fields: Array<{name: string; required: boolean}>, currentConfig: Record<string, any>) => {
    return fields
      .filter(field => !field.required && !(field.name in currentConfig))
      .map(field => field.name);
  };

  // Get default value for a field type
  const getDefaultValue = (type: string) => {
    switch (type) {
      case 'number':
        return 0;
      case 'boolean':
        return false;
      case 'array':
        return [];
      case 'object':
        return {};
      default:
        return '';
    }
  };

  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'form' | 'json') => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleSectionToggle = (section: string) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // Render a primitive field
  const renderPrimitiveField = (field: {name: string; type: string; description?: string; required: boolean}) => {
    const value = config[field.name] ?? getDefaultValue(field.type);
    const isRequired = field.required;

    // Provide clearer guidance for GCP key_pair_name
    const effectiveDescription =
      providerType === 'gcp' && field.name === 'key_pair_name'
        ? 'Full SSH public-key line to embed in VM metadata, e.g.  cb-agents:ssh-rsa AAAAB3... user@host'
        : field.description;

    const getInputType = () => {
      switch (field.type) {
        case 'string':
          return 'text';
        case 'number':
          return 'number';
        case 'boolean':
          return 'checkbox';
        default:
          return 'text';
      }
    };

    const inputType = getInputType();

    return (
      <Grid item xs={12} sm={6} key={field.name}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {inputType === 'checkbox' ? (
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(value)}
                  onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                  disabled={disabled}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {effectiveDescription || field.name.replace(/_/g, ' ')}
                    {isRequired && <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />}
                  </Typography>
                </Box>
              }
            />
          ) : (
            <TextField
              fullWidth
              label={effectiveDescription || field.name.replace(/_/g, ' ')}
              placeholder={providerType === 'gcp' && field.name === 'key_pair_name' ? 'cb-agents:ssh-rsa AAAAB3... user@host' : undefined}
              value={value}
              type={inputType}
              onChange={(e) => {
                const newValue = inputType === 'number' ? Number(e.target.value) : e.target.value;
                handleFieldChange(field.name, newValue);
              }}
              disabled={disabled}
              required={isRequired}
              helperText={effectiveDescription}
              InputProps={{
                endAdornment: isRequired ? null : (
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveField(field.name)}
                    disabled={disabled}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )
              }}
            />
          )}
        </Box>
      </Grid>
    );
  };

  // Render a nested object field
  const renderNestedObjectField = (objectField: {
    name: string;
    type: 'object';
    description?: string;
    required: boolean;
    properties: Array<{
      name: string;
      type: string;
      description?: string;
      required: boolean;
      items?: {
        type: string;
        properties?: Array<{
          name: string;
          type: string;
          description?: string;
          required: boolean;
        }>;
      };
    }>;
  }) => {
    const isExpanded = expandedSections.includes(objectField.name);
    const objectValue = config[objectField.name] || {};
    const availableOptionalFields = getAvailableOptionalFields(objectField.properties, objectValue);

    return (
      <Accordion 
        key={objectField.name} 
        expanded={isExpanded}
        onChange={() => handleSectionToggle(objectField.name)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
              {objectField.name}
            </Typography>
            {objectField.required && <Chip label="Required" size="small" color="error" />}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {/* Render existing fields */}
            {objectField.properties.map(prop => {
              const value = objectValue[prop.name] ?? getDefaultValue(prop.type);
              const isRequired = prop.required;

              // Handle array fields
              if (prop.type === 'array' && prop.items) {
                const arrayValue = Array.isArray(value) ? value : [];
                
                return (
                  <Grid item xs={12} key={prop.name}>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2">
                          {prop.description || prop.name.replace(/_/g, ' ')}
                          {isRequired && <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />}
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleAddArrayItem(objectField.name, prop.name)}
                          disabled={disabled}
                        >
                          Add {prop.name.replace(/_/g, ' ').slice(0, -1)}
                        </Button>
                      </Box>
                      
                      {arrayValue.map((item, index) => (
                        <Card key={index} sx={{ mb: 1, p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {prop.name.replace(/_/g, ' ').slice(0, -1)} #{index + 1}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveArrayItem(objectField.name, prop.name, index)}
                              disabled={disabled}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          
                          {prop.items?.properties ? (
                            <Grid container spacing={2}>
                              {prop.items?.properties?.map(itemProp => {
                                // Special handling for instance type field
                                if (itemProp.name === 'type' && prop.name === 'instance_types') {
                                  if (providerType === 'aws') {
                                    return <Grid item xs={12} sm={6} key={itemProp.name}>
                                      <Autocomplete freeSolo disabled={disabled}
                                        options={supportedInstanceTypes.map(option => option.instance_type)}
                                        value={item.type || ''}
                                        onChange={(_, next) => handleInstanceTypeChange(objectField.name, prop.name, index, next || '')}
                                        onInputChange={(_, next, reason) => {
                                          if (reason === 'input') handleInstanceTypeChange(objectField.name, prop.name, index, next);
                                        }}
                                        renderInput={params => <TextField {...params} label="Instance type" required={itemProp.required}
                                          helperText="Select or enter an AWS instance type, such as c7i.xlarge." />} />
                                    </Grid>;
                                  }
                                  return (
                                    <Grid item xs={12} sm={6} key={itemProp.name}>
                                      <FormControl fullWidth required={itemProp.required}>
                                        <InputLabel>{itemProp.description || itemProp.name.replace(/_/g, ' ')}</InputLabel>
                                        <Select
                                          value={item[itemProp.name] ?? ''}
                                          onChange={(e) => handleInstanceTypeChange(objectField.name, prop.name, index, e.target.value)}
                                          disabled={disabled}
                                          label={itemProp.description || itemProp.name.replace(/_/g, ' ')}
                                        >
                                          {supportedInstanceTypes.map(instanceType => (
                                            <MenuItem key={instanceType.instance_type} value={instanceType.instance_type}>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2">{instanceType.display_name}</Typography>
                                                <Chip 
                                                  label={instanceType.architecture} 
                                                  size="small" 
                                                  color={instanceType.architecture === 'arm64' ? 'secondary' : 'primary'}
                                                />
                                              </Box>
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                  );
                                }

                                if (itemProp.name === 'bots_per_host' && prop.name === 'instance_types') {
                                  const capacity = item.bots_per_host ?? config.container_fleet?.bots_per_host ?? 5;
                                  const invalid = !Number.isInteger(capacity) || capacity < 1 || capacity > 32;
                                  return <Grid item xs={12} sm={6} key={itemProp.name}>
                                    <TextField fullWidth label="Bots per host" type="number" value={capacity}
                                      disabled={disabled} required error={invalid} inputProps={{ min: 1, max: 32, step: 1 }}
                                      onChange={e => handleUpdateArrayItem(objectField.name, prop.name, index, itemProp.name,
                                        e.target.value === '' ? '' : Number(e.target.value))}
                                      helperText={invalid ? 'Enter a whole number from 1 to 32.' :
                                        'Applies to new Docker hosts of this type. Existing hosts keep their capacity. VM mode uses one bot per machine.'} />
                                  </Grid>;
                                }
                                
                                // Regular text field for other properties
                                return (
                                  <Grid item xs={12} sm={6} key={itemProp.name}>
                                    <TextField
                                      fullWidth
                                      label={itemProp.description || itemProp.name.replace(/_/g, ' ')}
                                      value={item[itemProp.name] ?? getDefaultValue(itemProp.type)}
                                      type={itemProp.type === 'number' ? 'number' : 'text'}
                                      onChange={(e) => {
                                        const newValue = itemProp.type === 'number' ? Number(e.target.value) : e.target.value;
                                        handleUpdateArrayItem(objectField.name, prop.name, index, itemProp.name, newValue);
                                      }}
                                      disabled={disabled}
                                      required={itemProp.required}
                                      helperText={itemProp.description}
                                    />
                                  </Grid>
                                );
                              })}
                            </Grid>
                          ) : (
                            <TextField
                              fullWidth
                              label={prop.description || prop.name.replace(/_/g, ' ')}
                              value={item}
                              type={prop.items?.type === 'number' ? 'number' : 'text'}
                              onChange={(e) => {
                                const newValue = prop.items?.type === 'number' ? Number(e.target.value) : e.target.value;
                                handleUpdateArrayItem(objectField.name, prop.name, index, '', newValue);
                              }}
                              disabled={disabled}
                            />
                          )}
                        </Card>
                      ))}
                      
                      {arrayValue.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No {prop.name.replace(/_/g, ' ')} configured. Click "Add" to add one.
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                );
              }

              // Handle regular fields
              return (
                <Grid item xs={12} sm={6} key={prop.name}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {prop.type === 'boolean' ? (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(value)}
                            onChange={(e) => handleNestedFieldChange(objectField.name, prop.name, e.target.checked)}
                            disabled={disabled}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">
                              {prop.description || prop.name.replace(/_/g, ' ')}
                              {isRequired && <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />}
                            </Typography>
                          </Box>
                        }
                      />
                    ) : (
                      <TextField
                        fullWidth
                        label={(() => {
                          if (providerType === 'gcp' && prop.name === 'key_pair_name') {
                            return 'SSH public-key line (e.g. cb-agents:ssh-rsa AAAAB3...)';
                          }
                          return prop.description || prop.name.replace(/_/g, ' ');
                        })()}
                        placeholder={providerType === 'gcp' && prop.name === 'key_pair_name' ? 'cb-agents:ssh-rsa AAAAB3... user@host' : undefined}
                        value={value}
                        type={prop.type === 'number' ? 'number' : 'text'}
                        onChange={(e) => {
                          const newValue = prop.type === 'number' ? Number(e.target.value) : e.target.value;
                          handleNestedFieldChange(objectField.name, prop.name, newValue);
                        }}
                        disabled={disabled}
                        required={isRequired}
                        helperText={providerType === 'gcp' && prop.name === 'key_pair_name' ? 'Enter the full public-key line including username:' : prop.description}
                        InputProps={{
                          endAdornment: isRequired ? null : (
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveNestedField(objectField.name, prop.name)}
                              disabled={disabled}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )
                        }}
                      />
                    )}
                  </Box>
                </Grid>
              );
            })}

            {/* Optional fields section */}
            {availableOptionalFields.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1, backgroundColor: 'action.hover' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    Optional Fields
                  </Typography>
                  <Grid container spacing={1}>
                    {availableOptionalFields.map(fieldName => {
                      const field = objectField.properties.find(p => p.name === fieldName);
                      if (!field) return null;
                      
                      return (
                        <Grid item xs={12} sm={6} key={fieldName}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            p: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            backgroundColor: 'background.paper'
                          }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {field.description || field.name.replace(/_/g, ' ')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {field.type}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => handleAddNestedField(objectField.name, fieldName, getDefaultValue(field.type))}
                              disabled={disabled}
                              variant="outlined"
                            >
                              Add
                            </Button>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };



  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Provider Configuration
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
        >
          <ToggleButton value="form">
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Form
          </ToggleButton>
          <ToggleButton value="json">
            <CodeIcon fontSize="small" sx={{ mr: 1 }} />
            JSON
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {providerType === 'aws' && <AwsComputeSettings providerId={providerId} config={config} onChange={onChange}
        regionTypes={regionTypes} onRegionTypesChange={onRegionTypesChange} onValidityChange={onValidityChange} disabled={disabled} />}
      {viewMode === 'form' ? (
        <Box>
          {/* Render primitive fields first */}
          {parsedSchema.primitiveFields.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Configuration Fields
              </Typography>
              <Grid container spacing={2}>
                {parsedSchema.primitiveFields.map(field => renderPrimitiveField(field))}
              </Grid>
              
              {/* Optional fields section */}
              {getAvailableOptionalFields(parsedSchema.primitiveFields, config).length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    Optional Fields
                  </Typography>
                  <Grid container spacing={1}>
                    {getAvailableOptionalFields(parsedSchema.primitiveFields, config).map(fieldName => {
                      const field = parsedSchema.primitiveFields.find(f => f.name === fieldName);
                      if (!field) return null;
                      
                      return (
                        <Grid item xs={12} sm={6} key={fieldName}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            p: 1,
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 1,
                            backgroundColor: 'action.hover'
                          }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {field.description || field.name.replace(/_/g, ' ')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {field.type}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => handleAddField(fieldName, getDefaultValue(field.type))}
                              disabled={disabled}
                              variant="outlined"
                            >
                              Add
                            </Button>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}
            </Box>
          )}

          {/* Render nested object fields */}
          {parsedSchema.objectFields.map(objectField => {
            if (providerType !== 'aws' || objectField.name !== 'compute') return renderNestedObjectField(objectField);
            if (config.bot_runtime === 'docker') return null;
            const remaining = { ...objectField, properties: objectField.properties.filter(prop => !['instance_types', 'default_instance_type'].includes(prop.name)) };
            return remaining.properties.length ? renderNestedObjectField(remaining) : null;
          })}
          
          {/* Credentials Section */}
          {credentialsSchema && Object.keys(credentialsSchemaData).length > 0 && (
            <Accordion 
              expanded={expandedSections.includes('credentials')}
              onChange={() => handleSectionToggle('credentials')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  Credentials (Encrypted)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Credentials are encrypted and stored securely. Existing credentials cannot be viewed, only updated.
                </Alert>
                <Grid container spacing={2}>
                  {Object.entries(credentialsSchemaData).map(([fieldName, fieldConfig]: [string, any]) => (
                    <Grid item xs={12} key={fieldName}>
                      <TextField
                        fullWidth
                        label={fieldConfig.description || fieldName}
                        type={fieldConfig.masked ? 'password' : 'text'}
                        multiline={fieldConfig.multiline}
                        rows={fieldConfig.multiline ? 4 : 1}
                        required={credentialsSchema.required?.includes(fieldName)}
                        helperText={fieldConfig.description}
                        onChange={(e) => handleCredentialChange(fieldName, e.target.value)}
                        disabled={disabled}
                      />
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      ) : (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            JSON Configuration Editor
          </Typography>
          
          {jsonError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>JSON Error:</strong> {jsonError}
            </Alert>
          )}
          
          <TextField
            fullWidth
            multiline
            rows={20}
            value={jsonString}
            onChange={(e) => handleJsonChange(e.target.value)}
            disabled={disabled}
            error={!!jsonError}
            helperText={jsonError ? 'Please fix the JSON syntax error above' : 'Edit the JSON configuration directly. Changes will be applied in real-time if the JSON is valid.'}
            sx={{ 
              '& .MuiInputBase-input': { 
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              } 
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ProviderConfigForm;
